import PropTypes from "prop-types";
import * as React from "react";
import {
  Alert,
  Button,
  CopyButton,
  Dialog,
  Skeleton,
  TextInput,
} from "../../../themeV2";
import { downloadDataUrl } from "../../../util/downloadFile";
import * as logger from "../../../util/logger";
import { checkoutLinkFor, linkWarningFor, qrFileNameFor } from "./checkoutLink";
import css from "./CheckoutLinkDialog.module.css";

const renderQrCode = async (url) => {
  const { default: QRCode } = await import("qrcode");
  const [svg, png] = await Promise.all([
    QRCode.toString(url, { type: "svg", margin: 1 }),
    QRCode.toDataURL(url, { width: 1024, margin: 2 }),
  ]);
  return { svg, png };
};

const useQrCode = (url) => {
  const [qrCode, setQrCode] = React.useState(null);

  React.useEffect(() => {
    const subscription = { active: true };
    renderQrCode(url)
      .then((rendered) => subscription.active && setQrCode(rendered))
      .catch(logger.error);
    return () => {
      subscription.active = false;
    };
  }, [url]);

  return qrCode;
};

export default function CheckoutLinkDialog({ product, onClose }) {
  const [open, setOpen] = React.useState(true);
  const { url } = checkoutLinkFor({
    origin: window.location.origin,
    productId: product.productId,
  });
  const qrCode = useQrCode(url);
  const warning = linkWarningFor(product);

  return (
    <Dialog.Root
      open={open}
      onOpenChange={setOpen}
      onOpenChangeComplete={(isOpen) => {
        if (!isOpen) onClose();
      }}
    >
      <Dialog.Popup size="sm">
        <Dialog.Title>Checkout link</Dialog.Title>
        <Dialog.Description>
          Opens checkout with {product.name} in the cart, where the customer can
          personalize it and choose add-ons.
        </Dialog.Description>

        <Dialog.Body className={css.body}>
          {warning && <Alert severity="warning">{warning}</Alert>}

          <TextInput
            label="Link"
            value={url}
            readOnly
            trailing={<CopyButton text={url} label="Copy link" />}
          />

          <div className={css.qrCode}>
            {qrCode ? (
              <div
                role="img"
                aria-label="QR code for the checkout link"
                className={css.qrImage}
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{ __html: qrCode.svg }}
              />
            ) : (
              <Skeleton variant="rect" className={css.qrImage} />
            )}
          </div>
        </Dialog.Body>

        <Dialog.Actions>
          <Button
            variant="text"
            disabled={!qrCode}
            onClick={() => downloadDataUrl(qrCode.png, qrFileNameFor(product))}
          >
            Download QR code
          </Button>
          <Button onClick={() => setOpen(false)}>Done</Button>
        </Dialog.Actions>
      </Dialog.Popup>
    </Dialog.Root>
  );
}

CheckoutLinkDialog.propTypes = {
  product: PropTypes.shape({
    productId: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    isActive: PropTypes.bool,
    vaultedUntil: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.instanceOf(Date),
    ]),
    isSoldOut: PropTypes.bool,
  }).isRequired,
  onClose: PropTypes.func.isRequired,
};
